import React, { useState, useEffect, useCallback, useRef } from 'react';
import friendlyError from '../../utils/friendlyError';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import {
  FaBolt, FaExclamationTriangle, FaSyncAlt, FaBullhorn, FaFileAlt, FaQuestionCircle,
  FaShareAlt, FaTasks, FaUsers, FaComments, FaBan, FaSitemap, FaListUl, FaChartBar,
  FaEye, FaLayerGroup, FaArrowRight, FaCopy, FaCheck, FaPrint, FaHistory, FaTrash,
  FaFireAlt, FaPaperPlane, FaRobot, FaUser, FaMagic, FaSpinner, FaBrain, FaDatabase, FaPenNib,
} from 'react-icons/fa';
import {
  TechShell, TechPageHeader, TechSection, TechTabs, TechButton, TechButtonGhost, TechChip, TechTextarea, TechInput,
  StatusPill, Empty, Loading, ErrorBox, borderSoft, mono, accent, danger, warning, success,
} from '../../styles/techComponents';
import { armarUrgencia, listUrgencias, getUrgencia, deleteUrgencia, chatUrgencia, generarInforme } from '../../services/urgenciaService';
import PulsoSocial from './PulsoSocial';

const EJEMPLOS = [
  'Suspensión de empleados en las empresas de Chouhy por represalias del municipio',
  'El municipio (Passaglia) subió las tasas y hay bronca de vecinos',
  'Nota negativa contra Chouhy en un diario local',
  'Denuncia por una licitación irregular del municipio',
];

const ICONS = {
  diagnostico: FaEye, mensajes: FaBullhorn, comunicado: FaFileAlt, qa: FaQuestionCircle,
  posteos: FaShareAlt, plan_accion: FaTasks, plan: FaTasks, stakeholders: FaUsers,
  argumentario: FaComments, riesgos: FaExclamationTriangle, no_hacer: FaBan,
  escenarios: FaSitemap, checklist: FaListUl, datos: FaChartBar, monitoreo: FaEye,
};

const Intro = styled.div` display:flex;flex-direction:column;gap:0.6rem;margin-bottom:0.8rem; `;
const Chips = styled.div` display:flex;flex-wrap:wrap;gap:0.4rem; `;
const ExChip = styled.button` font-size:0.78rem;padding:0.3rem 0.6rem;border:1px solid ${borderSoft};border-radius:999px;
  background:rgba(11,19,32,0.5);color:var(--text-secondary);cursor:pointer;text-align:left;
  &:hover{border-color:${accent};color:var(--text-primary)} `;
const Punch = styled.div` border:1px solid rgba(0,180,216,0.35);border-left:3px solid ${accent};border-radius:8px;
  padding:1rem 1.1rem;margin:0.6rem 0 0.9rem;background:rgba(0,180,216,0.06);
  p{margin:0;font-size:1.02rem;line-height:1.5;color:var(--text-primary)} `;
const Grid = styled.div` display:grid;grid-template-columns:repeat(auto-fill,minmax(320px,1fr));gap:0.8rem; `;
const Mod = styled.div` border:1px solid ${(p) => p.$p === 'alta' ? 'rgba(239,68,68,0.3)' : borderSoft};border-radius:10px;
  background:rgba(11,19,32,0.45);padding:0.9rem 1rem;display:flex;flex-direction:column; `;
const ModHead = styled.div` display:flex;align-items:flex-start;gap:0.5rem;margin-bottom:0.5rem;
  h4{margin:0;font-size:0.98rem;display:flex;align-items:center;gap:0.45rem;flex:1}
  svg.ti{color:${accent};flex-shrink:0} `;
const ModMeta = styled.div` display:flex;gap:0.35rem;flex-wrap:wrap;margin-bottom:0.5rem; `;
const Pri = styled(TechChip)` background:${(p) => p.$p === 'alta' ? 'rgba(239,68,68,0.16)' : p.$p === 'media' ? 'rgba(245,158,11,0.16)' : 'rgba(255,255,255,0.06)'};
  color:${(p) => p.$p === 'alta' ? danger : p.$p === 'media' ? warning : 'var(--text-secondary)'}; `;
const Fuente = styled(TechChip)` background:${(p) => p.$f === 'plataforma' ? 'rgba(0,180,216,0.14)' : 'rgba(168,85,247,0.14)'};
  color:${(p) => p.$f === 'plataforma' ? accent : '#c084fc'}; `;
const Resumen = styled.p` margin:0 0 0.5rem;font-size:0.84rem;color:var(--text-secondary);line-height:1.45; `;
const Texto = styled.pre` white-space:pre-wrap;font-family:inherit;font-size:0.88rem;line-height:1.55;color:var(--text-primary);margin:0 0 0.3rem; `;
const Items = styled.ul` margin:0.1rem 0;padding-left:1.1rem;li{font-size:0.88rem;line-height:1.5;color:var(--text-primary);margin-bottom:0.3rem} `;
const Par = styled.div` margin-bottom:0.5rem;
  .k{font-size:0.86rem;font-weight:600;color:var(--text-primary);margin-bottom:0.15rem}
  .v{font-size:0.85rem;color:var(--text-secondary);line-height:1.5} `;
const Post = styled.div` border:1px solid ${borderSoft};border-radius:8px;padding:0.5rem 0.6rem;margin-bottom:0.4rem;background:rgba(0,0,0,0.2);
  .red{font-family:${mono};font-size:0.62rem;letter-spacing:0.08em;text-transform:uppercase;color:${accent};margin-bottom:0.25rem}
  .tx{font-size:0.85rem;color:var(--text-primary);line-height:1.5;white-space:pre-wrap} `;
const ModFoot = styled.div` display:flex;gap:0.4rem;margin-top:auto;padding-top:0.6rem;align-items:center;flex-wrap:wrap; `;
const IconBtn = styled.button` display:inline-flex;align-items:center;gap:0.3rem;font-size:0.74rem;padding:0.25rem 0.5rem;
  border:1px solid ${borderSoft};border-radius:6px;background:transparent;color:var(--text-secondary);cursor:pointer;
  &:hover{border-color:${accent};color:var(--text-primary)} `;
const HistRow = styled.div` display:flex;align-items:center;gap:0.5rem;padding:0.5rem 0.6rem;border:1px solid ${borderSoft};border-radius:8px;margin-bottom:0.4rem;
  background:rgba(11,19,32,0.4);cursor:pointer;&:hover{border-color:${accent}}
  .s{flex:1;font-size:0.86rem;color:var(--text-primary)} .m{font-size:0.72rem;color:var(--text-secondary);font-family:${mono}} `;
// Informe
const Seccion = styled.div` border:1px solid ${borderSoft};border-radius:10px;padding:0.9rem 1.1rem;margin-bottom:0.7rem;background:rgba(11,19,32,0.4);
  h4{margin:0 0 0.5rem;font-size:1rem;color:var(--text-primary)}
  pre{white-space:pre-wrap;font-family:inherit;font-size:0.9rem;line-height:1.6;color:var(--text-secondary);margin:0} `;
// Chat
const ChatWrap = styled.div` display:flex;flex-direction:column;gap:0.6rem; `;
const Msgs = styled.div` display:flex;flex-direction:column;gap:0.6rem;max-height:52vh;overflow-y:auto;padding:0.2rem; `;
const Bubble = styled.div` max-width:82%;align-self:${(p) => p.$me ? 'flex-end' : 'flex-start'};
  border:1px solid ${(p) => p.$me ? 'rgba(0,180,216,0.3)' : borderSoft};border-radius:12px;padding:0.6rem 0.8rem;
  background:${(p) => p.$me ? 'rgba(0,180,216,0.08)' : 'rgba(11,19,32,0.55)'};
  .rol{display:flex;align-items:center;gap:0.35rem;font-family:${mono};font-size:0.6rem;letter-spacing:0.1em;text-transform:uppercase;color:var(--text-secondary);margin-bottom:0.3rem}
  .tx{font-size:0.9rem;line-height:1.55;color:var(--text-primary);white-space:pre-wrap} `;
const Cambio = styled.div` display:flex;align-items:center;gap:0.35rem;font-size:0.76rem;color:${success};margin-top:0.4rem; `;
const ChatBar = styled.div` display:flex;gap:0.5rem;align-items:flex-end; `;

// --- Panel de progreso animado en vivo mientras la IA arma el informe + kit ---
const spin = keyframes` to { transform: rotate(360deg); } `;
const pulse = keyframes` 0%,100%{ opacity:1 } 50%{ opacity:0.45 } `;
const rise = keyframes` from{ opacity:0; transform:translateY(6px) } to{ opacity:1; transform:none } `;
const shimmer = keyframes` 0%{ background-position:-180% 0 } 100%{ background-position:180% 0 } `;
const glow = keyframes` 0%,100%{ box-shadow:0 0 0 0 rgba(0,180,216,0.35) } 50%{ box-shadow:0 0 0 8px rgba(0,180,216,0) } `;

const ProgWrap = styled.div`
  border:1px solid rgba(0,180,216,0.3); border-radius:12px; padding:1.3rem 1.5rem; margin:0.4rem 0 0.6rem;
  background:linear-gradient(180deg, rgba(0,180,216,0.07), rgba(11,19,32,0.4));
`;
const ProgHead = styled.div` display:flex; align-items:center; gap:0.7rem; margin-bottom:0.3rem;
  .ic{ width:38px; height:38px; border-radius:10px; display:grid; place-items:center; background:rgba(0,180,216,0.14); color:${accent};
    animation:${glow} 2s ease-in-out infinite; flex-shrink:0; }
  .ic svg{ animation:${pulse} 1.6s ease-in-out infinite; font-size:1.1rem; }
  h4{ margin:0; font-size:1.02rem; color:var(--text-primary); }
  .sub{ font-size:0.78rem; color:var(--text-secondary); font-family:${mono}; }
`;
const ProgBar = styled.div` height:6px; border-radius:4px; margin:0.9rem 0 1rem; overflow:hidden; background:rgba(255,255,255,0.06);
  > span{ display:block; height:100%; border-radius:4px; width:${({ $w }) => $w}%; transition:width 0.6s ease;
    background:linear-gradient(90deg, ${accent}, #38d39f, ${accent}); background-size:200% 100%; animation:${shimmer} 1.6s linear infinite; }
`;
const StepRow = styled.div` display:flex; align-items:center; gap:0.65rem; padding:0.35rem 0; animation:${rise} 0.35s ease both;
  .dot{ width:24px; height:24px; border-radius:50%; display:grid; place-items:center; flex-shrink:0; font-size:0.72rem;
    border:1px solid ${({ $st }) => $st === 'done' ? 'rgba(56,211,159,0.5)' : $st === 'now' ? 'rgba(0,180,216,0.6)' : borderSoft};
    background:${({ $st }) => $st === 'done' ? 'rgba(56,211,159,0.14)' : $st === 'now' ? 'rgba(0,180,216,0.14)' : 'transparent'};
    color:${({ $st }) => $st === 'done' ? success : $st === 'now' ? accent : 'var(--text-disabled)'}; }
  .dot .sp{ animation:${spin} 0.9s linear infinite; }
  .tx{ font-size:0.9rem; color:${({ $st }) => $st === 'pending' ? 'var(--text-disabled)' : 'var(--text-primary)'};
    ${({ $st }) => $st === 'now' ? css`animation:${pulse} 1.6s ease-in-out infinite;` : ''} }
`;

const PASOS = [
  { icon: FaEye, t: 'Leyendo y clasificando la situación', d: 'Severidad, foco y ángulo de ataque.' },
  { icon: FaDatabase, t: 'Juntando el estado real de la plataforma', d: 'Pulso social, reputación, prensa, licitaciones, reclamos y territorio.' },
  { icon: FaBrain, t: 'La IA analiza y arma el kit de módulos', d: 'Diagnóstico, mensajes, comunicado, Q&A, posteos y plan 48h.' },
  { icon: FaPenNib, t: 'Redactando el informe político completo', d: 'Cierra el análisis con datos reales, sin inventar métricas.' },
];

function ProgresoIA() {
  const [active, setActive] = useState(0);
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const step = setInterval(() => setActive((a) => Math.min(a + 1, PASOS.length - 1)), 3200);
    const tick = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => { clearInterval(step); clearInterval(tick); };
  }, []);
  const pct = Math.min(95, Math.round(((active + 0.5) / PASOS.length) * 100));
  const ActiveIcon = PASOS[active].icon;
  return (
    <ProgWrap>
      <ProgHead>
        <div className="ic"><ActiveIcon /></div>
        <div>
          <h4>La IA está trabajando en tu crisis…</h4>
          <span className="sub">{PASOS[active].d} · {secs}s</span>
        </div>
      </ProgHead>
      <ProgBar $w={pct}><span /></ProgBar>
      {PASOS.map((p, i) => {
        const st = i < active ? 'done' : i === active ? 'now' : 'pending';
        return (
          <StepRow key={i} $st={st}>
            <span className="dot">{st === 'done' ? <FaCheck /> : st === 'now' ? <FaSpinner className="sp" /> : i + 1}</span>
            <span className="tx">{p.t}</span>
          </StepRow>
        );
      })}
    </ProgWrap>
  );
}

function moduloToText(m) {
  const L = [`## ${m.titulo}`];
  if (m.resumen) L.push(m.resumen);
  if (m.texto) L.push(m.texto);
  (m.items || []).forEach((i) => L.push('• ' + i));
  (m.pares || []).forEach((p) => L.push(`${p.k}\n${p.v}`));
  (m.posteos || []).forEach((p) => L.push(`[${p.red}] ${p.texto}`));
  return L.join('\n');
}

function ModuloCard({ m, onGo }) {
  const [copied, setCopied] = useState(false);
  const Icon = ICONS[m.tipo] || FaLayerGroup;
  const copy = () => navigator.clipboard?.writeText(moduloToText(m)).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600); });
  return (
    <Mod $p={m.prioridad}>
      <ModHead><Icon className="ti" /><h4>{m.titulo}</h4></ModHead>
      <ModMeta>
        <Pri $p={m.prioridad}>{m.prioridad}</Pri>
        <Fuente $f={m.fuente}>{m.fuente === 'plataforma' ? 'de la plataforma' : m.fuente === 'mixto' ? 'mixto' : 'estrategia externa'}</Fuente>
      </ModMeta>
      {m.resumen && <Resumen>{m.resumen}</Resumen>}
      {m.texto && <Texto>{m.texto}</Texto>}
      {m.items?.length > 0 && <Items>{m.items.map((i, k) => <li key={k}>{i}</li>)}</Items>}
      {m.pares?.length > 0 && <div>{m.pares.map((p, k) => <Par key={k}><div className="k">{p.k}</div><div className="v">{p.v}</div></Par>)}</div>}
      {m.posteos?.length > 0 && <div>{m.posteos.map((p, k) => <Post key={k}><div className="red">{p.red}</div><div className="tx">{p.texto}</div></Post>)}</div>}
      <ModFoot>
        <IconBtn onClick={copy}>{copied ? <><FaCheck style={{ color: success }} /> Copiado</> : <><FaCopy /> Copiar</>}</IconBtn>
        {m.seccion && <IconBtn onClick={() => onGo(m.seccion)}><FaArrowRight /> Abrir {m.seccion}</IconBtn>}
      </ModFoot>
    </Mod>
  );
}

function Urgencia() {
  const navigate = useNavigate();
  const [situacion, setSituacion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [urg, setUrg] = useState(null);
  const [degradado, setDegradado] = useState([]);
  const [hist, setHist] = useState([]);
  // Arranca visible: la lista de urgencias anteriores se ve al entrar, sin tener
  // que abrir "Historial". El botón la sigue plegando, y se pliega al abrir una.
  const [showHist, setShowHist] = useState(true);
  const [tab, setTab] = useState('informe');
  const [chatMsg, setChatMsg] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [informeBusy, setInformeBusy] = useState(false);
  const msgsRef = useRef(null);

  const loadHist = useCallback(async () => {
    try { setHist(await listUrgencias()); } catch { /* silencioso */ }
  }, []);
  useEffect(() => { loadHist(); }, [loadHist]);

  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [urg?.chat, chatBusy]);

  const setActiveTabFor = (u) => setTab((u?.informe?.secciones?.length ? 'informe' : 'kit'));

  const hacerInforme = useCallback(async (id) => {
    if (!id) return;
    setInformeBusy(true);
    try {
      const informe = await generarInforme(id);
      setUrg((u) => (u && u.id === id ? { ...u, informe } : u));
    } catch { /* el usuario puede reintentar desde la pestaña Informe */ }
    finally { setInformeBusy(false); }
  }, []);

  const armar = useCallback(async (texto) => {
    const s = (texto ?? situacion).trim();
    if (s.length < 5) { setError('Describí la situación (unas palabras al menos).'); return; }
    setLoading(true); setError(null); setUrg(null); setDegradado([]);
    try {
      const res = await armarUrgencia(s);
      setUrg(res.urgencia); setDegradado(res.degradado || []); setActiveTabFor(res.urgencia); loadHist();
      hacerInforme(res.urgencia.id); // el informe se genera en segundo plano (request aparte)
    } catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  }, [situacion, loadHist, hacerInforme]);

  const abrir = useCallback(async (id) => {
    setLoading(true); setError(null); setShowHist(false);
    try { const u = await getUrgencia(id); setUrg(u); setSituacion(u.situacion); setActiveTabFor(u); }
    catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  }, []);

  // Permite entrar directo a un kit con /urgencia?id=N. Lo usan el banner de
  // crisis y los botones de las alertas de Telegram, que enlazan al kit que la
  // IA armó sola. Corre una sola vez, al montar.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('id');
    if (id) abrir(Number(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const borrar = useCallback(async (id, ev) => {
    ev.stopPropagation();
    try { await deleteUrgencia(id); loadHist(); } catch { /* noop */ }
  }, [loadHist]);

  const enviarChat = useCallback(async () => {
    const m = chatMsg.trim();
    if (!m || !urg?.id || chatBusy) return;
    setChatBusy(true); setChatMsg('');
    // eco optimista
    setUrg((u) => ({ ...u, chat: [...(u.chat || []), { role: 'user', content: m }] }));
    try {
      const res = await chatUrgencia(urg.id, m);
      setUrg((u) => ({ ...u, modulos: res.modulos, informe: res.informe, chat: res.chat }));
    } catch (e) {
      setUrg((u) => ({ ...u, chat: [...(u.chat || []), { role: 'assistant', content: 'Error: ' + (friendlyError(e)) }] }));
    } finally { setChatBusy(false); }
  }, [chatMsg, urg, chatBusy]);

  const copiarInforme = () => {
    const txt = (urg?.informe?.secciones || []).map((s) => `# ${s.titulo}\n${s.contenido}`).join('\n\n');
    navigator.clipboard?.writeText(txt);
  };

  const modulos = urg?.modulos || [];
  const secciones = urg?.informe?.secciones || [];
  const chatLog = urg?.chat || [];
  const meta = urg?.estado || {};

  const TABS = [
    { id: 'informe', label: `Informe${informeBusy ? ' …' : ` (${secciones.length})`}` },
    { id: 'social', label: 'Pulso social' },
    { id: 'kit', label: `Kit de acción (${modulos.length})` },
    { id: 'chat', label: 'Ajustar con IA' },
  ];

  return (
    <TechShell>
      <TechPageHeader
        title="Sala de Crisis"
        dossier="RESPUESTA RÁPIDA · IA"
        subtitle="Describí la situación y la IA arma un informe político completo + un kit de acción con módulos dinámicos. Después lo ajustás por chat: la IA modifica y crea módulos según lo que le pidas."
        actions={
          <>
            <TechButtonGhost onClick={() => setShowHist((v) => !v)}><FaHistory /> Historial ({hist.length})</TechButtonGhost>
            {urg && <TechButtonGhost onClick={() => window.print()}><FaPrint /> Imprimir</TechButtonGhost>}
          </>
        }
      />

      {showHist && (
        <TechSection title="Urgencias anteriores" icon={<FaHistory />}>
          {hist.length === 0 && <Empty>Todavía no generaste ninguna.</Empty>}
          {hist.map((h) => (
            <HistRow key={h.id} onClick={() => abrir(h.id)}>
              <span className="s">{h.situacion}</span>
              <span className="m">{h.modulos} módulos · {String(h.created_at).slice(0, 10)}</span>
              <IconBtn onClick={(e) => borrar(h.id, e)}><FaTrash /></IconBtn>
            </HistRow>
          ))}
        </TechSection>
      )}

      <TechSection title="¿Qué situación hay que atacar?" icon={<FaFireAlt />}>
        <Intro>
          <TechTextarea rows={3}
            placeholder="Ej: Suspensión de empleados en las empresas de Chouhy por represalias del municipio…"
            value={situacion} onChange={(e) => setSituacion(e.target.value)} />
          <Chips>{EJEMPLOS.map((ex, i) => <ExChip key={i} onClick={() => { setSituacion(ex); armar(ex); }}>{ex}</ExChip>)}</Chips>
          <div>
            <TechButton onClick={() => armar()} disabled={loading}>
              <FaBolt /> {loading ? 'Armando informe y kit…' : 'Armar con IA'}
            </TechButton>
          </div>
        </Intro>
      </TechSection>

      {error && <ErrorBox><FaExclamationTriangle /> {error}</ErrorBox>}
      {loading && <ProgresoIA />}

      {urg && !loading && (
        <>
          {(meta.lectura || meta.severidad) && (
            <TechSection title="Lectura de la situación" icon={<FaExclamationTriangle />}
              action={meta.severidad && <Pri $p={meta.severidad}>severidad {meta.severidad}</Pri>}>
              {degradado.length > 0 && (
                <StatusPill style={{ marginBottom: '0.5rem', color: warning }}>
                  <FaExclamationTriangle /> Datos que no cargaron: {degradado.join(', ')}
                </StatusPill>
              )}
              {meta.lectura && <Punch><p>{meta.lectura}</p></Punch>}
              <Resumen>Sobre: <strong style={{ color: 'var(--text-primary)' }}>{urg.situacion}</strong></Resumen>
            </TechSection>
          )}

          <TechTabs tabs={TABS} active={tab} onChange={setTab} />

          {tab === 'informe' && (
            <TechSection title="Informe político" icon={<FaFileAlt />}
              action={secciones.length > 0
                ? <TechButtonGhost onClick={copiarInforme}><FaCopy /> Copiar informe</TechButtonGhost>
                : <TechButtonGhost onClick={() => hacerInforme(urg.id)} disabled={informeBusy}><FaSyncAlt /> {informeBusy ? 'Generando…' : 'Generar informe'}</TechButtonGhost>}>
              {informeBusy && secciones.length === 0 && <Loading>La IA está redactando el informe político completo…</Loading>}
              {!informeBusy && secciones.length === 0 && <Empty>Todavía no hay informe. Tocá «Generar informe» o pedíselo a la IA desde «Ajustar con IA».</Empty>}
              {secciones.map((s, i) => (
                <Seccion key={i}><h4>{s.titulo}</h4><pre>{s.contenido}</pre></Seccion>
              ))}
            </TechSection>
          )}

          {tab === 'social' && (
            <TechSection title="Pulso social" icon={<FaChartBar />}>
              <Resumen>Ánimo social, posts, likes, comentarios y noticias reales de la plataforma — con gráficas. Por defecto se filtra a lo <strong>relevante a la situación</strong>; podés cambiar a «Todo». La IA usa estos datos para el informe y el kit.</Resumen>
              <PulsoSocial situacion={urg.situacion} />
            </TechSection>
          )}

          {tab === 'kit' && (
            <TechSection title="Kit de acción" icon={<FaLayerGroup />}
              action={<TechButtonGhost onClick={() => armar(urg.situacion)}><FaSyncAlt /> Regenerar todo</TechButtonGhost>}>
              {modulos.length === 0 && <Empty>Sin módulos. Pedíle a la IA que arme alguno desde el chat.</Empty>}
              <Grid>{modulos.map((m, i) => <ModuloCard key={i} m={m} onGo={(s) => navigate(s)} />)}</Grid>
            </TechSection>
          )}

          {tab === 'chat' && (
            <TechSection title="Ajustar con IA" icon={<FaComments />}>
              <Resumen>Pedíle cambios en lenguaje natural: «hacé el comunicado más duro», «agregá un módulo con el argumento legal», «sacá los posteos de Facebook», «sumá una sección al informe sobre el impacto electoral». La IA modifica y crea módulos, y edita el informe.</Resumen>
              <ChatWrap>
                <Msgs ref={msgsRef}>
                  {chatLog.length === 0 && <Empty>Todavía no hay mensajes. Escribí abajo para empezar a ajustar.</Empty>}
                  {chatLog.map((m, i) => (
                    <Bubble key={i} $me={m.role === 'user'}>
                      <div className="rol">{m.role === 'user' ? <><FaUser /> vos</> : <><FaRobot /> IA</>}</div>
                      <div className="tx">{m.content}</div>
                      {Array.isArray(m.cambios) && m.cambios.map((c, k) => <Cambio key={k}><FaMagic /> {c}</Cambio>)}
                    </Bubble>
                  ))}
                  {chatBusy && <Bubble><div className="rol"><FaRobot /> IA</div><div className="tx">Pensando y ajustando el kit…</div></Bubble>}
                </Msgs>
                <ChatBar>
                  <TechInput
                    placeholder="Pedile un cambio a la IA…"
                    value={chatMsg}
                    onChange={(e) => setChatMsg(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviarChat(); } }}
                    disabled={chatBusy}
                  />
                  <TechButton onClick={enviarChat} disabled={chatBusy || !chatMsg.trim()}><FaPaperPlane /> Enviar</TechButton>
                </ChatBar>
              </ChatWrap>
            </TechSection>
          )}
        </>
      )}
    </TechShell>
  );
}

export default Urgencia;
